/* eslint-env browser */
import React, { useEffect, useState } from 'react';
import Icon from 'brastrap/common/icon/Icon';
import { FormattedMessage } from 'react-intl';
import BraSizeGuideContainer from '../../../../shared/routes/Product/containers/modals/BraSizeGuideContainer';
import {
  BAND_SIZE,
  CUP_SIZE,
  dataLayerSelectItemOption,
} from '../../../../shared/utils/data-layer';

const SizeSelectorWidget = (props: Props) => {
  const [selectedBand, setSelectedBand] = useState(null);
  const [selectedCup, setSelectedCup] = useState(null);
  const [availableCupSizes, setAvailableCupSizes] = useState(null);
  const [sizeData, setSizeData] = useState(null);

  useEffect(() => {
    if (props.sizes.variants.length > 0 && props.sizes.value !== '') {
      setSizeData(props.sizes);

      if (typeof window !== 'undefined' && !selectedCup && !selectedBand) {
        const stringifiedObj = sessionStorage.getItem('selectedSkuDetails');
        const selectedSkuDetails = JSON.parse(stringifiedObj);

        const matchingBand =
          selectedSkuDetails &&
          props.sizes &&
          props.sizes.variants.find(
            variant => variant.value === selectedSkuDetails.bandSize
          );

        if (
          selectedSkuDetails?.styleCode === props?.styleCode &&
          matchingBand !== undefined &&
          matchingBand
        ) {
          setSelectedBand(selectedSkuDetails.bandSize);
          setSelectedCup(selectedSkuDetails.cupSize);
        }
      }
    }
  }, [props.sizes]);

  useEffect(() => {
    const arrayOfCupSizes = [];
    if (sizeData) {
      sizeData.variants.forEach(variant => {
        const unique = [
          ...new Set(variant.sizes.variants.map(size => size.value)),
        ];
        arrayOfCupSizes.push(...unique);
      });
      setAvailableCupSizes([...new Set(arrayOfCupSizes.sort())]);
    }
  }, [sizeData]);

  /* eslint-disable */
  useEffect(() => {
    props.onChange(null);

    if (selectedBand && sizeData) {
      let arrayOfCupSizes = [];
      
      sizeData &&
        sizeData.variants
          .find(variant => variant.value === selectedBand)
          .sizes.variants.forEach(variant => {
            arrayOfCupSizes.push(variant.value);
          });

      if (arrayOfCupSizes.includes(selectedCup)) {
        const selectedSku = getSelectedSku();
        props.onChange({ ...selectedSku });
      } else {
        setSelectedCup(null);
      }
      setAvailableCupSizes([...new Set(arrayOfCupSizes.sort())]);
    }
  }, [selectedBand]);
  /* eslint-enable */

  useEffect(() => {
    if (selectedBand && selectedCup) {
      const selectedSku = getSelectedSku();
      props.onChange({ ...selectedSku });
    }
  }, [selectedCup]);

  /**
   * Grab selected sku from data structure
   * @returns {object}
   */
  function getSelectedSku() {
    return (
      sizeData &&
      sizeData.variants
        .find(variant => variant.value === selectedBand)
        .sizes.variants.find(val => val.value === selectedCup)
    );
  }

  /**
   * Check if a cup size is available for the given band size provided
   * @param {string} size
   * @returns {boolean}
   */
  function isCupSizeAvailable(size) {
    if (selectedBand) {
      const selectedBandVariant = sizeData.variants.find(
        variant => variant.value === selectedBand
      );
      return selectedBandVariant?.sizes.variants.some(
        val => val.value === size && val.available
      );
    }

    return sizeData.variants.some(variant =>
      variant.sizes.variants.some(val => val.value === size && val.available)
    );
  }

  /**
   * Check there is an available cup size for current band
   * @param {string} currentBandSize
   * @returns {boolean}
   */
  function hasAnAvailableCupSize(currentBandSize) {
    if (currentBandSize) {
      return (
        currentBandSize &&
        sizeData.variants
          .find(variant => variant.value === currentBandSize)
          .sizes.variants.find(val => val.available)
      );
    }
    return true;
  }

  /**
   * Gets all relevant classes for selector
   * @param {string} variantValue
   * @param {string} currentlySelectedBand
   * @returns {string}
   */
  function getBandSelectorClasses(variantValue, currentlySelectedBand) {
    const isSelected = variantValue === currentlySelectedBand;
    const isAvailable = hasAnAvailableCupSize(variantValue);

    let classes = 'size-selector-widget__selector ';

    if (isSelected) {
      classes += isAvailable
        ? 'size-selector-widget__selector--selected '
        : 'size-selector-widget__selector--selectedAndUnavailable ';
    }

    if (!isAvailable) {
      classes += 'size-selector-widget__selector--unavailable ';
    }

    return classes;
  }

  return (
    <div className="size-selector-widget__container">
      <div className="size-selector-widget__bandContainer">
        <h1 className="size-selector-widget__bandHeader">
          <FormattedMessage
            id="size-selector-widget.band-size-title"
            defaultMessage="BAND SIZE"
          />
          &nbsp; {selectedBand && '|'}
          <span
            id="selected-band-size"
            className="size-selector-widget__header--currentSelectedItem"
          >
            {selectedBand}
          </span>
        </h1>
        {props.braSizeGuideUrl && (
          <BraSizeGuideContainer
            url={props.braSizeGuideUrl.slug}
            category={props.productCategory}
            locale={props.locale}
          />
        )}
      </div>

      <div className="size-selector-widget__selectors">
        {sizeData &&
          sizeData.variants.map(variant => (
            <div
              id={`band-selector-${variant.value}`}
              onClick={() => {
                setSelectedBand(variant.value);
                dataLayerSelectItemOption(
                  BAND_SIZE,
                  variant.value,
                  props.product
                );
              }}
              className={getBandSelectorClasses(variant.value, selectedBand)}
            >
              {variant.value}
            </div>
          ))}
      </div>

      <h1 className="size-selector-widget__cupHeader">
        <FormattedMessage
          id="size-selector-widget.cup-size-title"
          defaultMessage="CUP SIZE"
        />
        &nbsp; {selectedCup && '|'}
        <span
          id="selected-cup-size"
          className="size-selector-widget__header--currentSelectedItem"
        >
          {selectedCup}
        </span>
        <span>
          {props.locale === 'en-US' &&
            selectedCup &&
            props.mapSelectedUKCupSizeToUS(selectedCup)}
        </span>
      </h1>

      <div className="size-selector-widget__selectors">
        {
          /* eslint-disable */
          availableCupSizes &&
          availableCupSizes.map(size => (
            <div
              id={`cup-selector-${size}`}
              onClick={() => 
                {
                  setSelectedCup(size);
                  dataLayerSelectItemOption(CUP_SIZE, size, props.product);
                }}
              className={`${!isCupSizeAvailable(size) && size === selectedCup
                ? 'size-selector-widget__selector--selectedAndUnavailable'
                : size === selectedCup
                  ? 'size-selector-widget__selector--selected'
                  : ''
                } ${isCupSizeAvailable(size)
                  ? ''
                  : 'size-selector-widget__selector--unavailable'
                } size-selector-widget__selector`}
            >
              {size}
            </div>
              /* eslint-enable */
            ))
        }
      </div>

      {(selectedCup === 'F' || selectedCup === 'G') && (
        <div className="size-selector-widget__selectedFFCupSizeContainer">
          <Icon icon="information" />
          <span className="size-selector-widget__selectedFFCupSize">
            <FormattedMessage
              id="size-selector-widget.ff-cup-recommendation"
              defaultMessage={`If you're having trouble deciding between an F and G cup, we recommend trying an FF cup.`}
            />
          </span>
        </div>
      )}
    </div>
  );
};

export default SizeSelectorWidget;
